import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { DashboardShell } from "@/components/DashboardShell";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { CustomerFieldsForm } from "@/components/CustomerFieldsForm";
import { FmcsaLookupCard } from "@/components/FmcsaLookupCard";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { ChevronLeft } from "lucide-react";

export default function AdminCustomerNewPage() {
    const navigate = useNavigate();
    const [data, setData] = useState({});
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState("");

    const onSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (!data.company_name?.trim()) {
            setError("Company name is required");
            return;
        }
        setSubmitting(true);
        try {
            const { data: c } = await api.post("/admin/customers", data);
            toast.success("Customer created");
            navigate(`/admin/customers/${c.id}`);
        } catch (err) {
            setError(formatApiError(err));
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <DashboardShell>
            <div className="fade-up max-w-3xl">
                <Link to="/admin/customers" className="inline-flex items-center gap-1 text-sm text-slate-500 hover:text-slate-900 mb-6">
                    <ChevronLeft size={14} /> Customers
                </Link>
                <p className="text-xs font-medium tracking-[0.18em] uppercase text-blue-900 mb-3">
                    New customer
                </p>
                <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
                    Create a customer profile
                </h1>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">
                    You can create a customer record now and invite them later. Required fields can be left blank — the customer fills them on their first application.
                </p>

                <form onSubmit={onSubmit} className="mt-8 space-y-6" data-testid="customer-new-form">
                    <Card>
                        <CardContent className="p-6 space-y-6">
                            <CustomerFieldsForm data={data} onChange={setData} prefix="customer-new" />
                            {data.usdot_number && (
                                <div className="rounded-md border border-slate-200 bg-slate-50 p-4">
                                    <p className="text-xs font-medium tracking-[0.18em] uppercase text-slate-500 mb-2">FMCSA SAFER verification</p>
                                    <FmcsaLookupCard
                                        usdot={data.usdot_number}
                                        companyValues={{
                                            company_name: data.company_name || "",
                                            company_address: data.company_address || "",
                                            company_phone: data.company_phone || "",
                                            ein_tax_id: data.ein_tax_id || "",
                                        }}
                                        onPrefill={(patch) => setData((d) => ({ ...d, ...patch }))}
                                        testIdPrefix="customer-new-fmcsa"
                                    />
                                </div>
                            )}
                        </CardContent>
                    </Card>

                    {error && (
                        <div className="rounded-md bg-red-50 border border-red-100 px-3 py-2 text-sm text-red-700" data-testid="customer-new-error">
                            {error}
                        </div>
                    )}

                    <div className="flex gap-3">
                        <Button asChild variant="outline">
                            <Link to="/admin/customers" data-testid="customer-new-cancel">Cancel</Link>
                        </Button>
                        <Button type="submit" disabled={submitting} className="bg-blue-900 hover:bg-blue-800" data-testid="customer-new-submit">
                            {submitting ? "Creating…" : "Create customer"}
                        </Button>
                    </div>
                </form>
            </div>
        </DashboardShell>
    );
}
